import { useState, useEffect, useCallback } from 'react';
import SharedHeaderHomeowner from './SharedHeaderHomeowner';
// CB:BEGIN kpi-api-client
import { getEnvApiBaseUrl } from '../config/env';
// CB:END kpi-api-client

interface DataTrackingProps {
  onNavigate: (screen: string) => void;
}

// Event Tracker Class
class EventTracker {
  private events: Record<string, number> = {};
  // CB:BEGIN kpi-api-client
  private baseUrl: string;
  // CB:END kpi-api-client

  constructor() {
    this.loadFromStorage();
    // CB:BEGIN kpi-api-client
    const resolvedApiBase = getEnvApiBaseUrl();
    this.baseUrl = (resolvedApiBase && resolvedApiBase.trim().length > 0) ? resolvedApiBase : '';
    // CB:END kpi-api-client
  }

  async track(eventName: string) {
    // CB:BEGIN kpi-track-non-fatal
    // KPI tracking must NEVER break the React tree or cause unhandled promise rejections
    try {
      if (!this.events[eventName]) {
        this.events[eventName] = 0;
      }
      this.events[eventName] += 1;
      this.saveToStorage();
      console.log(`[EventTracker] Event logged: ${eventName}`, this.events);
    } catch (storageError) {
      // Even localStorage errors should not break tracking
      console.warn('[EventTracker] Storage error (ignored):', storageError);
    }
    
    // Fire-and-forget API call - never await in a way that blocks UI
    // Use .catch() to ensure no unhandled promise rejections
    fetch(`${this.baseUrl.replace(/\/$/, '')}/api/kpi/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eventName })
    }).catch((err: any) => {
      // KPI tracking must never break the UI - silently ignore all errors
      // In dev, skip logging when API is just down (avoid console spam)
      const isNetworkError =
        err?.message === 'Failed to fetch' ||
        (err?.message && /fetch|network|connection/i.test(String(err.message)));
      if ((import.meta as any).env?.DEV && !isNetworkError) {
        console.warn('[EventTracker] KPI track failed (ignored):', err);
      }
    });
    // CB:END kpi-track-non-fatal
  }

  getEvents() {
    return this.events;
  }

  getTotalEvents() {
    return Object.values(this.events).reduce((sum, count) => sum + count, 0);
  }

  async clearEvents() {
    this.events = {};
    this.saveToStorage();
    // CB:BEGIN kpi-api-client
    try {
      await fetch(`${this.baseUrl.replace(/\/$/, '')}/api/kpi/reset`, { method: 'DELETE' });
    } catch (err) {
      console.warn('KPI reset failed (non-fatal):', err);
    }
    // CB:END kpi-api-client
  }

  private saveToStorage() {
    localStorage.setItem("corebids_events", JSON.stringify(this.events));
  }

  private loadFromStorage() {
    const stored = localStorage.getItem("corebids_events");
    if (stored) {
      try {
        this.events = JSON.parse(stored);
      } catch (error) {
        console.error('Error parsing stored events:', error);
        this.events = {};
      }
    }
  }
}

// Create global instance
export const eventTracker = new EventTracker();

const DataTracking: React.FC<DataTrackingProps> = ({ onNavigate }) => {
  const [events, setEvents] = useState<Record<string, number>>({});
  const [totalEvents, setTotalEvents] = useState(0);
  // CB:BEGIN kpi-manual-refresh
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  // CB:END kpi-manual-refresh
  // CB:BEGIN kpi-manual-refresh
  const fetchKpis = useCallback(async () => {
    try {
      const apiBase = getEnvApiBaseUrl();
      const baseUrl =
        apiBase && apiBase.trim().length > 0
          ? apiBase.replace(/\/+$/, '')
          : '';

      const resp = await fetch(`${baseUrl.replace(/\/$/, '')}/api/kpi`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const nextEvents = (data && data.events) || {};
      const nextTotal = (data && data.total) || 0;
      setEvents(nextEvents);
      setTotalEvents(nextTotal);
      setLastRefreshedAt(new Date());
    } catch (err) {
      // Fallback to local cache if API unavailable
      const currentEvents = eventTracker.getEvents();
      setEvents(currentEvents);
      setTotalEvents(eventTracker.getTotalEvents());
      setLastRefreshedAt(new Date());
    }
  }, []);

  useEffect(() => {
    let alive = true;
    // Initial load
    fetchKpis();
    // Refresh every 2 seconds
    const interval = setInterval(() => { if (alive) fetchKpis(); }, 2000);
    return () => { alive = false; clearInterval(interval); };
  }, [fetchKpis]);
  // CB:END kpi-manual-refresh

  const handleClearEvents = async () => {
    await eventTracker.clearEvents();
    setEvents({});
    setTotalEvents(0);
    // CB:BEGIN kpi-manual-refresh
    setLastRefreshedAt(new Date());
    // CB:END kpi-manual-refresh
  };

  // CB:BEGIN kpi-manual-refresh
  const handleRefresh = async () => {
    await fetchKpis();
  };
  // CB:END kpi-manual-refresh

  const getEventCategory = (eventName: string): string => {
    if (eventName.includes('landing') || eventName.includes('page_view')) return 'Page Views';
    if (eventName.includes('form') || eventName.includes('submit') || eventName.includes('start')) return 'Form Interactions';
    if (eventName.includes('login') || eventName.includes('signup') || eventName.includes('auth')) return 'Authentication';
    if (eventName.includes('bid') || eventName.includes('estimate')) return 'Bid Requests';
    if (eventName.includes('dashboard') || eventName.includes('navigate')) return 'Navigation';
    if (eventName.includes('address') || eventName.includes('project')) return 'Project Data';
    return 'Other';
  };

  const categorizedEvents = Object.entries(events).reduce((acc, [event, count]) => {
    const category = getEventCategory(event);
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push({ event, count });
    return acc;
  }, {} as Record<string, Array<{ event: string; count: number }>>);

  const categoryColors = {
    'Page Views': '#3b82f6',
    'Form Interactions': '#10b981',
    'Authentication': '#f59e0b',
    'Bid Requests': '#ef4444',
    'Navigation': '#8b5cf6',
    'Project Data': '#06b6d4',
    'Other': '#6b7280'
  };

  return (
    <div className="container">
      <SharedHeaderHomeowner onNavigate={onNavigate} />
      <div className="content">
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px' }}>
          <div style={{ marginBottom: '32px' }}>
            <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px', color: '#1f2937' }}>
              CoreBids Data Tracking
            </h1>
            <p style={{ fontSize: '16px', color: '#6b7280' }}>
              Real-time analytics and KPI tracking for demo mode
            </p>
          </div>


          {/* Customer Analytics Section */}
          <div style={{ marginBottom: '32px' }}>
            {/* Customer Analytics Header */}
            <div style={{
              margin: '0 0 32px 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                width: '100%',
                height: '2px',
                background: 'linear-gradient(90deg, transparent, #e5e7eb, transparent)',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  background: 'white',
                  padding: '0 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Customer Analytics
                </div>
              </div>
            </div>

            {/* Conversion Funnel */}
            <div style={{
              marginBottom: '32px',
              padding: '24px',
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                marginBottom: '16px', 
                color: '#1f2937',
                textAlign: 'center'
              }}>
                Customer Conversion Funnel
              </h3>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                {/* Step 1: Landing Page */}
                <div style={{ textAlign: 'center', flex: '1', minWidth: '120px' }}>
                  <div style={{ 
                    fontSize: '24px', 
                    fontWeight: '700', 
                    color: '#3b82f6',
                    marginBottom: '4px'
                  }}>
                    {events['landing_page_view'] || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Landing Page</div>
                </div>

                {/* Arrow */}
                <div style={{ fontSize: '20px', color: '#d1d5db', alignSelf: 'center', marginTop: '12px' }}>→</div>

                {/* Step 2: Address Form */}
                <div style={{ textAlign: 'center', flex: '1', minWidth: '120px' }}>
                  <div style={{ 
                    fontSize: '24px', 
                    fontWeight: '700', 
                    color: '#10b981',
                    marginBottom: '4px'
                  }}>
                    {events['address_form_entered'] || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Address Form</div>
                </div>

                {/* Arrow */}
                <div style={{ fontSize: '20px', color: '#d1d5db', alignSelf: 'center', marginTop: '12px' }}>→</div>

                {/* Step 3: Estimate Form */}
                <div style={{ textAlign: 'center', flex: '1', minWidth: '120px' }}>
                  <div style={{ 
                    fontSize: '24px', 
                    fontWeight: '700', 
                    color: '#f59e0b',
                    marginBottom: '4px'
                  }}>
                    {events['estimate_form_entered'] || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Estimate Form</div>
                </div>

                {/* Arrow */}
                <div style={{ fontSize: '20px', color: '#d1d5db', alignSelf: 'center', marginTop: '12px' }}>→</div>

                {/* Step 4: Bid Request */}
                <div style={{ textAlign: 'center', flex: '1', minWidth: '120px' }}>
                  <div style={{ 
                    fontSize: '24px', 
                    fontWeight: '700', 
                    color: '#ef4444',
                    marginBottom: '4px'
                  }}>
                    {events['bid_request_submitted'] || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Bid Request</div>
                </div>

                {/* Arrow */}
                <div style={{ fontSize: '20px', color: '#d1d5db', alignSelf: 'center', marginTop: '12px' }}>→</div>

              </div>
            </div>
            
            <div style={{ 
              display: 'flex', 
              gap: '20px',
              justifyContent: 'space-between'
            }}>
              {/* Landing Page Views */}
              <div style={{
                background: 'linear-gradient(135deg, #e0e7ff, #c7d2fe)',
                color: '#3730a3',
                padding: '24px',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(99, 102, 241, 0.1)',
                flex: '1',
                border: '1px solid #c7d2fe'
              }}>
                <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                  Landing Page Views
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  Users who entered the landing page
                </div>
              </div>

              {/* Address Form Submissions */}
              <div style={{
                background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)',
                color: '#065f46',
                padding: '24px',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.1)',
                flex: '1',
                border: '1px solid #a7f3d0'
              }}>
                <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                  Address Form Submissions
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  Users who entered address and clicked Get My Estimate
                </div>
              </div>

              {/* Estimate Form Entries */}
              <div style={{
                background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                color: '#92400e',
                padding: '24px',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(245, 158, 11, 0.1)',
                flex: '1',
                border: '1px solid #fde68a'
              }}>
                <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                  Estimate Form Entries
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  Users who clicked Continue to Real Pro Estimates
                </div>
              </div>

              {/* Bid Requests Submitted */}
              <div style={{
                background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
                color: '#991b1b',
                padding: '24px',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(239, 68, 68, 0.1)',
                flex: '1',
                border: '1px solid #fecaca'
              }}>
                <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                  Bid Requests Submitted
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  Users who clicked Get 3 Bids button
                </div>
              </div>

              {/* Dashboard User Logins */}
              <div style={{
                background: 'linear-gradient(135deg, #e9d5ff, #d8b4fe)',
                color: '#6b21a8',
                padding: '24px',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(139, 92, 246, 0.1)',
                flex: '1',
                border: '1px solid #d8b4fe'
              }}>
                <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                  Dashboard User Logins
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  Users who logged in and entered customer dashboard
                </div>
              </div>
            </div>

            {/* Customer Funnel Chart */}
            <div style={{
              marginTop: '32px',
              padding: '24px',
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                marginBottom: '20px', 
                color: '#1f2937',
                textAlign: 'center'
              }}>
                Customer Conversion Funnel
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                {/* Step 1: Landing Page Views */}
                <div style={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: '600px' }}>
                  <div style={{ 
                    width: '100%', 
                    height: '50px',
                    background: 'linear-gradient(135deg, #e0e7ff, #c7d2fe)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 20px',
                    color: '#3730a3',
                    fontWeight: '600',
                    border: '1px solid #c7d2fe'
                  }}>
                    <span>Landing Page Views</span>
                    <span style={{ fontSize: '18px', fontWeight: '700' }}>{events['landing_page_view'] || 0}</span>
                  </div>
                </div>

                {/* Step 2: Address Form Submissions */}
                <div style={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: '600px' }}>
                  <div style={{ 
                    width: '85%', 
                    height: '50px',
                    background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 20px',
                    color: '#065f46',
                    fontWeight: '600',
                    margin: '0 auto',
                    border: '1px solid #a7f3d0'
                  }}>
                    <span>Address Form Submissions</span>
                    <span style={{ fontSize: '18px', fontWeight: '700' }}>{events['address_form_entered'] || 0}</span>
                  </div>
                </div>

                {/* Step 3: Estimate Form Entries */}
                <div style={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: '600px' }}>
                  <div style={{ 
                    width: '70%', 
                    height: '50px',
                    background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 20px',
                    color: '#92400e',
                    fontWeight: '600',
                    margin: '0 auto',
                    border: '1px solid #fde68a'
                  }}>
                    <span>Estimate Form Entries</span>
                    <span style={{ fontSize: '18px', fontWeight: '700' }}>{events['estimate_form_entered'] || 0}</span>
                  </div>
                </div>

                {/* Step 4: Bid Requests Submitted */}
                <div style={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: '600px' }}>
                  <div style={{ 
                    width: '55%', 
                    height: '50px',
                    background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 20px',
                    color: '#991b1b',
                    fontWeight: '600',
                    margin: '0 auto',
                    border: '1px solid #fecaca'
                  }}>
                    <span>Bid Requests Submitted</span>
                    <span style={{ fontSize: '18px', fontWeight: '700' }}>{events['bid_request_submitted'] || 0}</span>
                  </div>
                </div>

                {/* Step 5: Dashboard User Logins */}
                <div style={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: '600px' }}>
                  <div style={{ 
                    width: '40%', 
                    height: '50px',
                    background: 'linear-gradient(135deg, #e9d5ff, #d8b4fe)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 20px',
                    color: '#6b21a8',
                    fontWeight: '600',
                    margin: '0 auto',
                    border: '1px solid #d8b4fe'
                  }}>
                    <span>Dashboard User Logins</span>
                    <span style={{ fontSize: '18px', fontWeight: '700' }}>{events['dashboard_viewuser_login'] || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Visual Break */}
          <div style={{
            margin: '48px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              width: '100%',
              height: '2px',
              background: 'linear-gradient(90deg, transparent, #e5e7eb, transparent)',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'white',
                padding: '0 24px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Contractor Analytics
              </div>
            </div>
          </div>

          {/* Contractor Analytics Section */}
          <div style={{ marginBottom: '32px' }}>

            {/* Contractor Conversion Funnel */}
            <div style={{
              marginBottom: '32px',
              padding: '24px',
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                marginBottom: '16px', 
                color: '#1f2937',
                textAlign: 'center'
              }}>
                Contractor Conversion Funnel
              </h3>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                {/* Step 1: Contractor Landing Page */}
                <div style={{ textAlign: 'center', flex: '1', minWidth: '120px' }}>
                  <div style={{ 
                    fontSize: '24px', 
                    fontWeight: '700', 
                    color: '#3b82f6',
                    marginBottom: '4px'
                  }}>
                    {events['contractor_landing_page_view'] || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Contractor Landing</div>
                </div>

                {/* Arrow */}
                <div style={{ fontSize: '20px', color: '#d1d5db', alignSelf: 'center', marginTop: '12px' }}>→</div>

                {/* Step 2: Join As Contractor */}
                <div style={{ textAlign: 'center', flex: '1', minWidth: '120px' }}>
                  <div style={{ 
                    fontSize: '24px', 
                    fontWeight: '700', 
                    color: '#10b981',
                    marginBottom: '4px'
                  }}>
                    {events['join_as_contractor_clicked'] || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Join As Contractor</div>
                </div>

                {/* Arrow */}
                <div style={{ fontSize: '20px', color: '#d1d5db', alignSelf: 'center', marginTop: '12px' }}>→</div>

                {/* Step 3: Start Earning Today */}
                <div style={{ textAlign: 'center', flex: '1', minWidth: '120px' }}>
                  <div style={{ 
                    fontSize: '24px', 
                    fontWeight: '700', 
                    color: '#f59e0b',
                    marginBottom: '4px'
                  }}>
                    {events['start_earning_today_clicked'] || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Start Earning Today</div>
                </div>

                {/* Arrow */}
                <div style={{ fontSize: '20px', color: '#d1d5db', alignSelf: 'center', marginTop: '12px' }}>→</div>

                {/* Step 4: Bid Submission */}
                <div style={{ textAlign: 'center', flex: '1', minWidth: '120px' }}>
                  <div style={{ 
                    fontSize: '24px', 
                    fontWeight: '700', 
                    color: '#ef4444',
                    marginBottom: '4px'
                  }}>
                    {events['contractor_bid_submitted'] || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Bid Submission</div>
                </div>
              </div>
            </div>
            
            <div style={{ 
              display: 'flex', 
              gap: '20px',
              justifyContent: 'space-between'
            }}>
              {/* Contractor Landing Page Views */}
              <div style={{
                background: 'linear-gradient(135deg, #e0e7ff, #c7d2fe)',
                color: '#3730a3',
                padding: '24px',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(99, 102, 241, 0.1)',
                flex: '1',
                border: '1px solid #c7d2fe'
              }}>
                <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                  Contractor Landing Page Views
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  Contractors who visited the landing page
                </div>
              </div>

              {/* Join As Contractor Clicks */}
              <div style={{
                background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)',
                color: '#065f46',
                padding: '24px',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.1)',
                flex: '1',
                border: '1px solid #a7f3d0'
              }}>
                <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                  Join As Contractor Clicks
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  Contractors who clicked "Join As a Contractor"
                </div>
              </div>

              {/* Start Earning Today Clicks */}
              <div style={{
                background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                color: '#92400e',
                padding: '24px',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(245, 158, 11, 0.1)',
                flex: '1',
                border: '1px solid #fde68a'
              }}>
                <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                  Start Earning Today Clicks
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  Contractors who clicked "Start Earning Today"
                </div>
              </div>

              {/* Bid Submissions */}
              <div style={{
                background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
                color: '#991b1b',
                padding: '24px',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(239, 68, 68, 0.1)',
                flex: '1',
                border: '1px solid #fecaca'
              }}>
                <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                  Bid Submissions
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  Contractors who submitted actual bids
                </div>
              </div>
            </div>

            {/* Contractor Funnel Chart */}
            <div style={{
              marginTop: '32px',
              padding: '24px',
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                marginBottom: '20px', 
                color: '#1f2937',
                textAlign: 'center'
              }}>
                Contractor Conversion Funnel
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                {/* Step 1: Contractor Landing Page Views */}
                <div style={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: '600px' }}>
                  <div style={{ 
                    width: '100%', 
                    height: '50px',
                    background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 20px',
                    color: 'white',
                    fontWeight: '600'
                  }}>
                    <span>Contractor Landing Page Views</span>
                    <span style={{ fontSize: '18px', fontWeight: '700' }}>{events['contractor_landing_page_view'] || 0}</span>
                  </div>
                </div>

                {/* Step 2: Join As Contractor Clicks */}
                <div style={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: '600px' }}>
                  <div style={{ 
                    width: '80%', 
                    height: '50px',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 20px',
                    color: 'white',
                    fontWeight: '600',
                    margin: '0 auto'
                  }}>
                    <span>Join As Contractor Clicks</span>
                    <span style={{ fontSize: '18px', fontWeight: '700' }}>{events['join_as_contractor_clicked'] || 0}</span>
                  </div>
                </div>

                {/* Step 3: Start Earning Today Clicks */}
                <div style={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: '600px' }}>
                  <div style={{ 
                    width: '60%', 
                    height: '50px',
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 20px',
                    color: 'white',
                    fontWeight: '600',
                    margin: '0 auto'
                  }}>
                    <span>Start Earning Today Clicks</span>
                    <span style={{ fontSize: '18px', fontWeight: '700' }}>{events['start_earning_today_clicked'] || 0}</span>
                  </div>
                </div>

                {/* Step 4: Bid Submissions */}
                <div style={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: '600px' }}>
                  <div style={{ 
                    width: '40%', 
                    height: '50px',
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 20px',
                    color: 'white',
                    fontWeight: '600',
                    margin: '0 auto'
                  }}>
                    <span>Bid Submissions</span>
                    <span style={{ fontSize: '18px', fontWeight: '700' }}>{events['contractor_bid_submitted'] || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '24px',
            padding: '16px',
            background: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 4px 0' }}>
                Event Analytics
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                Data refreshes automatically every 2 seconds
              </p>
              {/* CB:BEGIN kpi-manual-refresh */}
              {lastRefreshedAt && (
                <p style={{ fontSize: '12px', color: '#9ca3af', margin: '6px 0 0 0' }}>
                  Last updated: {lastRefreshedAt.toLocaleString()}
                </p>
              )}
              {/* CB:END kpi-manual-refresh */}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {/* CB:BEGIN kpi-manual-refresh */}
              <button
                onClick={handleRefresh}
                style={{
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#1d4ed8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#2563eb';
                }}
              >
                Refresh Stats
              </button>
              {/* CB:END kpi-manual-refresh */}
              <button
                onClick={handleClearEvents}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#dc2626';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ef4444';
                }}
              >
                Clear All Data
              </button>
            </div>
          </div>


        </div>
      </div>
    </div>
  );
};

export default DataTracking;
